<template>
  <div class="desktop-draws-section">
    <div class="desktop-draws-header">
      <h2 class="desktop-section-title">1. {{ $t('draws.selectDraws') }}</h2>
    </div>
    
    <div class="desktop-draws-content">
      <div class="desktop-draws-grid">
        <div
          v-for="(draw, index) in props.draws"
          :key="draw.event_id"
          class="desktop-draw-item"
          :class="{ 'selected': props.selectedDraws[index] }"
          @click="toggleDrawSelection(index)"
        >
          <div class="desktop-draw-content">
            <div class="desktop-time-icon">
              <img src="/img/icons/timer.png" :alt="$t('forms.time')" />
            </div>
            <div class="desktop-draw-info">
              <span class="desktop-draw-date">{{ formatDate(new Date(draw.draw_date)) }}</span>
              <span class="desktop-draw-time">{{ formatTime(new Date(draw.draw_date)) }}</span>
            </div>
          </div>
          <label class="desktop-checkbox-wrapper" @click.stop>
            <input 
              type="checkbox" 
              :checked="props.selectedDraws[index]" 
              @change="handleCheckboxChange(index, $event)"
            />
            <span class="desktop-checkmark"></span>
          </label>
        </div>
      </div>
      
      <!-- Quick Actions -->
      <div class="desktop-quick-actions">
        <button 
          class="desktop-action-btn primary" 
          @click="selectAll"
          :disabled="allSelected"
        >
          {{ $t('draws.selectAll') }}
        </button>
        <button 
          class="desktop-action-btn secondary" 
          @click="clearAll"
          :disabled="selectedCount <= 1"
        >
          {{ $t('common.clearAll') }}
        </button>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, defineProps, defineEmits, watch } from 'vue';

interface Draw {
  event_id: string | number;
  draw_date: string;
}

interface Props {
  draws: Draw[];
  selectedDraws: boolean[];
}

const props = defineProps<Props>();

const emit = defineEmits<{
  'update-selected-draws': [index: number, checked: boolean];
  'toggle-draw-selection': [index: number];
}>();

// Computed properties
const selectedCount = computed(() => {
  return props.selectedDraws.filter(Boolean).length;
});

const allSelected = computed(() => {
  return props.selectedDraws.every(Boolean);
});

// Methods
// Ensure at least one draw is always selected
const ensureMinimumSelection = () => {
  const selectedCount = props.selectedDraws.filter(Boolean).length;
  if (selectedCount === 0 && props.draws.length > 0) {
    // Select the first draw if none are selected
    emit('update-selected-draws', 0, true);
  }
};

// Watch for changes in selectedDraws to ensure minimum selection
watch(() => props.selectedDraws, () => {
  ensureMinimumSelection();
}, { immediate: true });

const toggleDrawSelection = (index: number) => {
  emit('toggle-draw-selection', index);
};

const handleCheckboxChange = (index: number, event: Event) => {
  const target = event.target as HTMLInputElement;
  emit('update-selected-draws', index, target.checked);
};

const selectAll = () => {
  props.draws.forEach((_, index) => {
    if (!props.selectedDraws[index]) {
      emit('update-selected-draws', index, true);
    }
  });
};

const clearAll = () => {
  // Only clear if there are multiple draws selected
  // Keep at least one draw selected
  const selectedIndices = props.selectedDraws
    .map((selected, index) => selected ? index : -1)
    .filter(index => index !== -1);
  
  if (selectedIndices.length > 1) {
    // Clear all except the first one
    selectedIndices.slice(1).forEach(index => {
      emit('update-selected-draws', index, false);
    });
  }
};

const formatDate = (date: Date): string => {
  const day = date.getDate().toString().padStart(2, '0');
  const month = (date.getMonth() + 1).toString().padStart(2, '0');
  const year = date.getFullYear();
  return `${day}/${month}/${year}`;
};

const formatTime = (date: Date): string => {
  return date.toLocaleTimeString('en-US', {
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: false
  });
};
</script>

<script lang="ts">
export default {
  name: "DesktopDrawsSection",
};
</script>

<style scoped>
.desktop-draws-section {
  background: var(--bg-secondary);
  border-radius: 8px;
  margin-bottom: 12px;
  overflow: hidden;
}

.desktop-draws-header {
  padding: 12px 16px;
  background: var(--bg-tertiary);
  border-bottom: 1px solid var(--border-color);
}

.desktop-section-title {
  font-size: 16px;
  font-weight: 600;
  color: var(--text-primary);
  margin: 0;
}

.desktop-draws-content {
  padding: 12px 16px;
}

.desktop-draws-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 6px;
  margin-bottom: 16px;
}

.desktop-draw-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 8px 12px;
  background: var(--bg-bet-check-box, var(--bg-primary));
  border: 1px solid var(--border-color);
  border-radius: 6px;
  cursor: pointer;
  transition: all 0.2s ease;
}

.desktop-draw-item:hover {
  border-color: var(--accent-color);
  background: var(--bg-hover);
}

.desktop-draw-item.selected {
  border-color: var(--accent-color);
  background: var(--bg-bet-check-box-selected, var(--bg-hover));
}

.desktop-draw-content {
  display: flex;
  align-items: center;
  gap: 6px;
}

.desktop-time-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 16px;
  height: 16px;
}

.desktop-time-icon img {
  width: 14px;
  height: 14px;
  filter: brightness(0) saturate(100%) invert(20%) sepia(8%) saturate(1038%) hue-rotate(169deg) brightness(95%) contrast(86%);
}

.desktop-draw-info {
  display: flex;
  flex-direction: column;
  gap: 1px;
}

.desktop-draw-date {
  font-size: 11px;
  color: var(--text-muted);
  font-weight: 400;
}

.desktop-draw-time {
  font-size: 11px;
  color: var(--text-accent);
  font-weight: 600;
}

.desktop-checkbox-wrapper {
  position: relative;
  display: inline-block;
  width: 14px;
  height: 14px;
  cursor: pointer;
}

.desktop-checkbox-wrapper input {
  opacity: 0;
  width: 0;
  height: 0;
  position: relative;
  background: var(--bg-bet-check-box, var(--text-white));
}

.desktop-checkmark {
  position: absolute;
  top: 0;
  left: 0;
  width: 14px;
  height: 14px;
  background: var(--bg-bet-check-box, var(--text-white));
  border: 2px solid var(--border-color);
  border-radius: 2px;
  transition: all 0.2s ease;
}

.desktop-checkbox-wrapper input:checked ~ .desktop-checkmark {
  background: var(--accent-color);
  border-color: var(--accent-color);
}

.desktop-checkmark:after {
  content: "";
  position: absolute;
  display: none;
}

.desktop-checkbox-wrapper input:checked ~ .desktop-checkmark:after {
  display: block;
  content: '✔';
  font-size: 12px;
  line-height: 1;
}

.desktop-checkbox-wrapper .desktop-checkmark:after {
  left: 50%;
  top: 23%; 
  height: 5px;
  transform: translate(-50%, -50%);
}

.desktop-quick-actions {
  display: flex;
  gap: 8px;
  padding-top: 12px;
  border-top: 1px solid var(--border-color);
}

.desktop-action-btn {
  flex: 1;
  padding: 8px 12px;
  border: 1px solid var(--border-color);
  border-radius: 6px;
  background: var(--bg-primary);
  color: var(--text-primary);
  font-size: 12px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.desktop-action-btn:hover:not(:disabled) {
  border-color: var(--accent-color);
  background: var(--accent-color);
  color: white;
}

.desktop-action-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.desktop-action-btn.secondary {
  background: var(--bg-tertiary);
  color: var(--text-secondary);
  border: 1px solid var(--border-btn, var(--border-color));
}

.desktop-action-btn.secondary:hover:not(:disabled) {
  background: var(--accent-color);
  color: white;
  border-color: var(--accent-color);
}

.desktop-action-btn.primary {
  background: var(--accent-color, var(--bg-tertiary));
  color: var(--text-button, var(--text-secondary));
}

.desktop-action-btn.primary:hover:not(:disabled) {
  background: var(--accent-color);
  color: white;
  border-color: var(--accent-color);
}


/* Responsive adjustments */
@media (max-width: 1024px) {
  .desktop-draws-grid {
    grid-template-columns: repeat(2, 1fr);
  }
}

@media (max-width: 768px) {
  .desktop-draws-grid {
    grid-template-columns: 1fr;
  }
  
  .desktop-draw-item {
    padding: 12px 16px;
  }
  
  .desktop-draw-date {
    font-size: 12px;
  }
  
  .desktop-draw-time {
    font-size: 12px;
  }
  
  .desktop-quick-actions {
    flex-direction: column;
    gap: 6px;
  }
  
  .desktop-action-btn {
    padding: 10px 12px;
    font-size: 13px;
  }
}

</style>
